/**
 * Firebase auth error-code → user-facing message maps. Pure lookups,
 * extracted from auth.service.ts (the 1000-line file-size gate).
 */

const AUTH_ERROR_MESSAGES: Record<string, string> = {
  'auth/user-not-found': 'No account found with this email',
  'auth/wrong-password': 'Incorrect email or password',
  'auth/invalid-credential': 'Incorrect email or password',
  'auth/email-already-in-use': 'An account with this email already exists',
  'auth/weak-password': 'Password should be at least 6 characters',
  'auth/invalid-email': 'Please enter a valid email address',
  'auth/too-many-requests': 'Too many attempts. Please try again later',
  'auth/network-request-failed': 'Network error. Please check your connection',
  'auth/popup-closed-by-user': 'Sign-in was cancelled',
  'auth/popup-blocked': 'Popup was blocked. Please allow popups and try again',
  'auth/cancelled-popup-request': 'Sign-in was cancelled',
  'auth/account-exists-with-different-credential':
    'An account already exists with this email but different sign-in method',
  'auth/operation-not-allowed': 'This sign-in method is not enabled',
  'auth/user-disabled': 'This account has been disabled',
  'auth/requires-recent-login': 'Please sign in again to continue',
  'auth/user-mismatch': 'The credentials do not match the current user',
  'auth/invalid-verification-code': 'Invalid verification code',
  'auth/invalid-verification-id': 'Invalid verification ID',
  'auth/credential-already-in-use': 'This credential is already associated with a different user',
  'auth/email-change-needs-verification': 'Please verify your new email address',
  'auth/internal-error': 'An internal error occurred. Please try again',
  'auth/expired-action-code': 'This password reset link has expired. Please request a new one',
  'auth/invalid-action-code': 'This password reset link is invalid. Please request a new one',
};

/** Convert Firebase error codes to user-friendly messages. */
export function getAuthErrorMessage(errorCode: string): string {
  return AUTH_ERROR_MESSAGES[errorCode] ?? 'Authentication failed. Please try again';
}

const EMAIL_VERIFICATION_ERROR_MESSAGES: Record<string, string> = {
  'auth/expired-action-code':
    'This email verification link has expired. Please request a new verification email.',
  'auth/invalid-action-code':
    'This email verification link is invalid or has already been used. Please request a new verification email.',
  'auth/user-disabled': 'This account has been disabled. Please contact support.',
  'auth/user-not-found': 'No account found with this email address.',
  'auth/invalid-verification-code':
    'Invalid email verification code. Please request a new verification email.',
  'auth/invalid-verification-id':
    'Invalid email verification link. Please request a new verification email.',
};

/** User-friendly error message for email verification errors. */
export function getEmailVerificationErrorMessage(errorCode: string): string {
  return (
    EMAIL_VERIFICATION_ERROR_MESSAGES[errorCode] ??
    'Email verification failed. The link may be expired or invalid. Please request a new verification email.'
  );
}

/** The session the backend never accepted has been signed out. Signing out also
 *  clears a pending invitation token, so an invitee has to reopen the link. */
export function ssoHandshakeError(
  refusal: string | undefined,
  hadInvitation: boolean,
): Error & { code: string } {
  const reason = refusal ?? 'We could not finish signing you in.';
  const nextStep = refusal ? '' : 'Please try again.';
  const hint = hadInvitation ? 'Please open your invitation link and try again.' : nextStep;
  const sentence = /[.!?]$/.test(reason) ? reason : `${reason}.`;
  const failure = new Error(`${sentence} ${hint}`.trim()) as Error & { code: string };
  failure.code = 'bower/sso-handshake-failed';
  return failure;
}
